import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { connectPageService } from "../../../../application/domain/base/base-application-service";

import {
  ProcManagementListService,
  ContextProps,
} from "../../../../application/service/proc-management/procedure-list";
import { useAuthContextV2 } from "../../../../auth-state";
import Title from "../../../atoms/proc-management/title";
import { ProcsTable } from "../../../organisms/proc-management/list";
import { PortalTemplate } from "../../../template/portal/default-template";
import { ProcManagementR } from "../route";
import { parseQueryOfLocation } from "../../../../utils/url-utils";
import { StatusValue } from "../../../../application/domain/proc-management";
import { SearchMenu } from "../../../molecules/proc-management/list";
import { RBACHidden } from "../../../helpers/rbac/rbac";
import { PrimaryButton } from "@graffer-inc/console-design-kit";
import { defaultFetchStatuses } from "../../../../application/service/proc-management/proc-list-base";
import { ensureStatusValueArr } from "../../../../application/domain/proc-management/util/status-query-converter";
import { TtzkPortalR } from "../../ttzk-portal/route";

const ProcManagementProcedureList: React.FC<ContextProps> = ({
  fetchProcsByOrgId,
  procedures,
  paging,
  setAuthCtx,
}) => {
  const location = useLocation();
  const { page, q, status } = parseQueryOfLocation(location);
  const pageNum =
    Number.isInteger(Number(page)) && Number(page) > 0
      ? Number(page)
      : undefined;
  const queryStr = typeof q === "string" && q.length > 0 ? q : undefined;
  const selectedStatuses = React.useMemo(
    () =>
      typeof status === "string" && status.length > 0
        ? ensureStatusValueArr(status.split(","))
        : [],
    [status, ensureStatusValueArr],
  );
  const { auth } = useAuthContextV2();
  const orgId = auth.data?.orgId;

  React.useEffect(() => {
    setAuthCtx(auth.data);
  }, [setAuthCtx, auth]);

  React.useEffect(() => {
    if (typeof orgId === "string" && selectedStatuses.length > 0) {
      fetchProcsByOrgId({
        organizationId: orgId,
        page: pageNum,
        query: queryStr,
        statuses: selectedStatuses,
      });
    }
  }, [fetchProcsByOrgId, orgId, pageNum, queryStr, selectedStatuses]);

  const history = useHistory();
  const handleRowClick = React.useCallback(
    ({ procId }: { orgId: string; procId: string }) => {
      history.push(ProcManagementR.ProcedureDetail.link({ procId }));
    },
    [history],
  );
  const handlePageClick = React.useCallback(
    ({ page }: { page: number }) => {
      history.push(
        ProcManagementR.ProcedureList.link({
          page,
          q: queryStr,
          statuses: selectedStatuses,
        }),
      );
    },
    [history, queryStr, selectedStatuses],
  );

  const handleSearch = React.useCallback(
    (query: string) => {
      history.push(
        ProcManagementR.ProcedureList.link({
          q: query,
          statuses: selectedStatuses,
        }),
      );
    },
    [history, selectedStatuses],
  );

  const handleStatusFilter = React.useCallback(
    (statuses: StatusValue[]) => {
      history.push(
        ProcManagementR.ProcedureList.link({ q: queryStr, statuses }),
      );
    },
    [history, queryStr],
  );

  const handleClear = React.useCallback(() => {
    history.push(
      ProcManagementR.ProcedureList.link({ statuses: selectedStatuses }),
    );
  }, [history, selectedStatuses]);

  return (
    <PortalTemplate
      breadcrumbs={[
        { to: TtzkPortalR.Home.link({}), title: "Graffer ポータル" },
        { to: ProcManagementR.ProcedureList.link({}), title: "手続き一覧" },
      ]}
    >
      <Title
        text={
          <>
            <span className="icon mr-2">
              <i className="fas fa-database" />
            </span>
            手続き一覧
          </>
        }
      />
      <MainMenu
        query={queryStr}
        onSearch={handleSearch}
        onClear={handleClear}
      />
      {typeof orgId === "string" && (
        <ProcsTable
          procedures={procedures}
          orgId={orgId}
          paging={paging}
          selectedStatuses={selectedStatuses}
          defaultStatuses={[...defaultFetchStatuses]}
          query={queryStr}
          onSelectStatus={handleStatusFilter}
          onRowClick={handleRowClick}
          onPageClick={handlePageClick}
        />
      )}
    </PortalTemplate>
  );
};

const MainMenu: React.FC<{
  query?: string;
  onSearch: (query: string) => void;
  onClear: () => void;
}> = (props) => {
  const history = useHistory();
  return (
    <div className="columns">
      <RBACHidden requirePermissions="proc_management.procedures.search">
        <SearchMenu
          query={props.query}
          onSearch={props.onSearch}
          onClear={props.onClear}
        />
      </RBACHidden>
      <div className="column has-text-right buttons">
        <RBACHidden requirePermissions="proc_management.procedures.create">
          <PrimaryButton
            text="手続き新規作成"
            onClick={() => {
              history.push(ProcManagementR.ProcedureCreate.link({}));
            }}
          />
        </RBACHidden>
      </div>
    </div>
  );
};

export const ProcManagementProcedureListContainer = connectPageService(
  ProcManagementProcedureList,
  () => new ProcManagementListService(),
);
